import type { IWrapper } from 'atoms/WrapperBlock'
import type { BANNER_ASPECT_RATIO } from 'vars/banner-aspect-ratio'
import type { BANNER_PADDINGS } from 'vars/banner-indents'
import type { COLOR } from 'vars/color'

import { styled } from '@linaria/react'
import { ButtonSC } from 'atoms/Button'
import { WrapperAlignItems, WrapperJustifyContent } from 'atoms/WrapperBlock'
import { BANNER_BACK_BUTTON_INDENT } from 'vars/banner-back-button-indent'
import { BANNER_HEIGHT } from 'vars/banner-height'
import { FIXED_INDENTS } from 'vars/fixed-indents'

export const PosterContentSC = styled.div<
  IWrapper & {
    backButtonIndent?: BANNER_BACK_BUTTON_INDENT
    aspectRatio?: BANNER_ASPECT_RATIO
    paddings?: BANNER_PADDINGS
    backgroundColor?: COLOR
    bannerDescription?: boolean
  }
>`
  --posterContentPaddingBottom: var(${FIXED_INDENTS.INDENT_140});

  width: ${({ width }) => (width ? `var(${width})` : `0`)};
  max-width: 100%;
  aspect-ratio: ${({ aspectRatio }) =>
    aspectRatio ? `var(${aspectRatio})` : `0`};
  position: relative;
  max-height: calc(100vh - var(--posterContentPaddingBottom));
  /*
   * Compact details banners (600–1023) keep a 580px Figma shell so
   * banner-description's flex-end offset matches MIN/MAX frames. The token is
   * 0px elsewhere. min() keeps short viewports from overflowing past max-height.
   * Keep min-height as a static property: Linaria lifts prop callbacks into
   * CSS variables, and a whole declaration next to display:flex is invalid CSS.
   */
  min-height: ${({ bannerDescription }) =>
    bannerDescription
      ? `min(var(${BANNER_HEIGHT.DETAILS_BANNER_COMPACT}), calc(100vh - var(--posterContentPaddingBottom)))`
      : 'auto'};
  display: flex;
  flex-direction: column;
  align-items: ${({ alignItems }) => alignItems || WrapperAlignItems.START};
  justify-content: ${({ justifyContent }) =>
    justifyContent || WrapperJustifyContent.FLEX_START};
  padding: ${({ bannerDescription, paddings }) =>
    !bannerDescription && paddings?.length ? `var(${paddings}) ` : `0`};
  background: ${({ backgroundColor }) =>
    backgroundColor ? `var(${backgroundColor})` : 'none'};
  /* Solution for browser versions, where the 'aspect-ratio' property is not supported */
  @supports not (
    aspect-ratio: ${({ aspectRatio }) =>
        aspectRatio ? `var(${aspectRatio})` : `0`}
  ) {
    &::before {
      float: left;
      padding-top: ${({ aspectRatio }) => `calc(100% / var(${aspectRatio}))`};
      content: '';
    }

    &::after {
      display: block;
      content: '';
      clear: both;
    }
  }

  /* Back button styles */
  > ${ButtonSC} {
    position: absolute;
    top: ${({ backButtonIndent }) =>
      `var(${backButtonIndent ?? BANNER_BACK_BUTTON_INDENT.SIZE_20})`};
  }
`
